import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "../lib/api";
import { ArrowLeft, Dumbbell, Sparkles } from "lucide-react";
import { Button } from "../components/ui/button";
import { toast } from "sonner";

export default function ExerciseDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [ex, setEx] = useState(null);
  const [imageData, setImageData] = useState(null);
  const [genLoading, setGenLoading] = useState(false);

  useEffect(() => { (async () => { const { data } = await api.get(`/exercises/${id}`); setEx(data); })(); }, [id]);

  const generateImage = async () => {
    if (!ex) return;
    setGenLoading(true);
    try {
      const { data } = await api.post("/ai/exercise-image", {
        prompt: `A person correctly performing ${ex.name}, targeting ${ex.muscle_groups.join(" and ")}. 3D clip-art cartoon style, bright studio lighting, no text, white background.`,
      });
      setImageData(`data:${data.mime_type};base64,${data.data}`);
    } catch (e) {
      toast.error("Image generation failed");
    } finally { setGenLoading(false); }
  };

  if (!ex) return <div className="p-6 text-neutral-400">Loading…</div>;

  return (
    <div className="max-w-3xl mx-auto p-4 sm:p-6">
      <button onClick={() => nav(-1)} className="flex items-center text-neutral-400 hover:text-blue-500 text-xs uppercase tracking-widest mb-4">
        <ArrowLeft className="w-4 h-4 mr-1" /> Back
      </button>
      <div className="text-xs tracking-[0.3em] uppercase text-blue-500 font-bold">{ex.category}</div>
      <h1 className="font-display text-4xl sm:text-5xl uppercase leading-none mt-2 mb-2">{ex.name}</h1>
      <div className="flex flex-wrap gap-2 mb-6">
        {ex.muscle_groups.map((m) => (
          <span key={m} className="px-2 py-1 text-xs tracking-wider uppercase border border-neutral-700 text-neutral-300">{m}</span>
        ))}
        <span className="px-2 py-1 text-xs tracking-wider uppercase border border-neutral-700 text-neutral-300">{ex.equipment}</span>
        <span className="px-2 py-1 text-xs tracking-wider uppercase border border-blue-500 text-blue-500">{ex.difficulty}</span>
      </div>

      <div className="aspect-video relative bg-neutral-900 border border-neutral-800 flex items-center justify-center overflow-hidden mb-6">
        {imageData ? (
          <img src={imageData} alt={ex.name} className="w-full h-full object-contain" />
        ) : ex.youtube_id ? (
          <iframe
            className="w-full h-full"
            src={`https://www.youtube.com/embed/${ex.youtube_id}`}
            title={ex.name}
            allowFullScreen
            data-testid="youtube-embed"
          />
        ) : (
          <Dumbbell className="w-16 h-16 text-neutral-700" />
        )}
      </div>

      <Button data-testid="gen-image-btn" onClick={generateImage} disabled={genLoading} variant="outline" className="bg-transparent border-neutral-700 text-white rounded-none uppercase tracking-wider font-display h-10 mb-8">
        <Sparkles className="w-4 h-4 mr-2" /> {genLoading ? "Generating…" : "Generate 3D Clip-Art"}
      </Button>

      <div className="mb-8">
        <h2 className="font-display text-2xl uppercase mb-3">How to Perform</h2>
        <ol className="space-y-2">
          {ex.instructions.map((step, i) => (
            <li key={i} className="flex gap-3 items-start border-b border-neutral-900 pb-2">
              <span className="font-display text-blue-500 text-xl">{i + 1}</span>
              <span className="text-neutral-300 text-sm leading-relaxed pt-1">{step}</span>
            </li>
          ))}
        </ol>
      </div>

      <div>
        <h2 className="font-display text-2xl uppercase mb-3">Muscles Worked</h2>
        <div className="flex flex-wrap gap-3">
          {ex.muscle_groups.map((m) => (
            <div key={m} className="border border-neutral-800 p-3 min-w-[100px]">
              <div className="text-xs tracking-[0.2em] uppercase text-neutral-500">Target</div>
              <div className="font-display text-lg uppercase text-blue-500 mt-1">{m}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
